import { create } from 'zustand';

/* Room Parents can have multiple classes + a school-wide Master page per school.
   Contexts come from the live schoolClassDetails on /myProfile — empty until it
   loads, and AppLayout holds the app back until then. */
const useContextStore = create((set, get) => ({
  contexts: [],
  currentContextId: null,

  switchContext: (id) => set({ currentContextId: id }),

  currentContext: () => get().contexts.find((c) => c.id === get().currentContextId),

  /* schoolClassDetails: [{ _id, name, photo, classes: [{ _id, name, photo,
     networkType: 'class' | 'subGroup', parentClassId, publicURL }] }] — the API
     flattens each class together with the sub groups joined under it.
     publicURL is the network's share link (short URL when the API minted one),
     which the Links page shows as the group chat link.
     → one Master context per school + one context per class/sub group. */
  setFromSchools: (schools) => {
    const masters = schools.map((s) => ({
      id: s._id,
      name: `${s.name} Master`,
      sub: `${s.name} · School-wide`,
      type: 'master',
      mark: '🏫',
      photo: s.photo || null,
      schoolId: s._id,
      schoolName: s.name,
    }));
    const classes = schools.flatMap((s) =>
      (s.classes || []).map((c) => ({
        id: c._id,
        name: c.name,
        sub: s.name,
        type: 'class',
        networkType: c.networkType || null,
        parentClassId: c.parentClassId || null,
        publicURL: c.publicURL || '',
        mark: '🍀',
        photo: c.photo || null,
        schoolId: s._id,
        schoolName: s.name,
      }))
    );
    const contexts = [...masters, ...classes];
    if (!contexts.length) return;
    const current = get().currentContextId;
    const stillExists = contexts.some((c) => c.id === current);
    /* land on something the switcher actually lists (see isSwitcherVisible) */
    const firstVisible = classes.find(isSwitcherVisible) || masters[0];
    set({
      contexts,
      currentContextId: stillExists ? current : firstVisible.id,
    });
  },

  resetContexts: () => set({ contexts: [], currentContextId: null }),
}));

export const selectCurrentContext = (s) => s.contexts.find((c) => c.id === s.currentContextId);

/* Client decision (2026-09-22): the switcher lists schools and sub groups only —
   plain classes are hidden. This is a display filter: the API still returns them
   and they stay in `contexts`, because the Room Parents "Class" picker and the
   master dashboard's class count are about real classes. Anything the API
   doesn't label is shown rather than silently lost. */
export const isSwitcherVisible = (c) => c.type === 'master' || c.networkType !== 'class';

/* 'master' | 'class' — events, sign-ups etc. are scoped by this. */
export const scopeOfContext = (ctx) => (ctx.type === 'master' ? 'master' : 'class');

export default useContextStore;
